// Aligner compliance, lab side.
//
//  • CompliancePanel — a tab on the case workspace: is this patient wearing their trays, what do
//    their photos show, and what is the doctor currently being asked to approve.
//  • WfCompliancePage — the chase list: every case with a patient app, worst wear first.
//
// The lab is not the treating clinician, so nothing here offers a clinical judgement. It reports
// what was recorded and flags who to chase.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  alignerQuote,
  type AlignerDiscount,
  formatDate,
  formatFileSize,
  PHOTO_POSE_LABELS,
  wearHours,
  type CaseCompliance,
  type ComplianceQueue,
  type ProgressPhoto,
  type WfFile,
  turnaroundSurchargeRupees,
  type WfPriority,
} from '@alignova/shared';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useAlignerPricing } from '../../lib/useAlignerPricing.ts';
import { PageHeader } from '../../ui/primitives.tsx';
import { SkeletonList, Skeleton } from '../../ui/mobile.tsx';
import { ComplianceBadge, ComplianceStats, LowWearNotice, WearChart, WearStatus } from '../../ui/compliance.tsx';
import { IconAlert, IconChevronRight } from '../../ui/icons.tsx';
import { Btn, Empty, Tabs, fmtDT, inputCls } from './wfUi.tsx';
import { DiscountInput } from './DiscountInput.tsx';
import { ArchTrayInputs } from './WfProforma.tsx';
import { fileDownloadUrl, wfApi } from './workflowApi.ts';

/* -- case panel -------------------------------------------------------------------------------- */

type PanelTab = 'overview' | 'wear' | 'photos' | 'timeline';

export function CompliancePanel({
  caseId,
  files,
  trays,
  priority,
  onFilesChanged,
}: {
  caseId: string;
  files: WfFile[];
  trays?: number | null;
  priority?: WfPriority | null;
  onFilesChanged: () => void;
}) {
  const [c, setC] = useState<CaseCompliance | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<PanelTab>('overview');
  const load = useCallback(() => {
    wfApi
      .compliance(caseId)
      .then((r) => {
        setC(r);
        setErr(null);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [caseId]);
  useEffect(load, [load]);
  useLiveEvent<{ id?: string }>('workflow.changed', (p) => {
    if (!p?.id || p.id === caseId) load();
  });

  if (err) return <div className="text-sm text-status-urgent-fg">{err}</div>;
  if (!c)
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );

  const photoUrl = (p: ProgressPhoto) => fileDownloadUrl(caseId, p.fileId, true);

  return (
    <div className="space-y-4">
      <Tabs<PanelTab>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'wear', label: 'Wear log', count: c.days.filter((d) => d.seconds > 0).length },
          { key: 'photos', label: 'Photos', count: c.photos.length },
          { key: 'timeline', label: 'Timeline' },
        ]}
      />

      {tab === 'overview' && (
        <div className="space-y-4">
          <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-ink">Aligner wear</h2>
                {c.hasPatientApp && <ComplianceBadge adherence={c.adherence7} />}
              </div>
              <WearStatus c={c} />
            </div>

            {c.hasPatientApp ? (
              <>
                <LowWearNotice c={c}>
                  {' '}
                  Worth a call before the next tray change{c.trays.nextChangeDate ? ` on ${formatDate(c.trays.nextChangeDate)}` : ''}.
                </LowWearNotice>
                <div className="mt-3">
                  <WearChart days={c.days} targetHours={c.targetHours} />
                </div>
                <div className="mt-4">
                  <ComplianceStats c={c} />
                </div>
                {c.trays.total && (
                  <p className="mt-3 text-sm text-muted">
                    Tray <span className="tabnum font-semibold text-ink">{c.trays.current ?? 1}</span> of{' '}
                    <span className="tabnum">{c.trays.total}</span>
                    {c.trays.nextChangeDate ? ` · next change ${formatDate(c.trays.nextChangeDate)}` : ''}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted">
                This case has no patient app yet. Issue a patient login from the Overview tab and the wear
                record starts building from the first day they use it.
              </p>
            )}
          </section>

          {c.hasPatientApp && <RemindPatient caseId={caseId} />}
          <ShareForApproval caseId={caseId} files={files} trays={trays} priority={priority} onChanged={onFilesChanged} />
        </div>
      )}

      {tab === 'wear' && <WearLog c={c} caseId={caseId} onSaved={load} />}
      {tab === 'photos' && <PhotoReview c={c} urlFor={photoUrl} />}
      {tab === 'timeline' && <ComplianceTimeline c={c} />}
    </div>
  );
}

/* -- wear log + manual entry ------------------------------------------------------------------- */

function WearLog({ c, caseId, onSaved }: { c: CaseCompliance; caseId: string; onSaved: () => void }) {
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await wfApi.recordWear(caseId, { day, hours: Number(hours), note: note || undefined });
      setHours('');
      setNote('');
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Newest first: a reviewer is asking "how has this week been", not "how did July go".
  const rows = [...c.days].reverse();

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
        <h2 className="text-sm font-semibold text-ink">Record wear by hand</h2>
        <p className="mb-3 mt-0.5 text-xs text-muted">
          For wear a patient reports by phone or at a visit. It writes to the same daily record the
          app fills, so the chart shows one series - not an app one and a staff one.
        </p>
        {err && <div className="mb-2 text-sm text-status-urgent-fg">{err}</div>}
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-muted">
            <span className="mb-1 block">Day</span>
            <input type="date" className={`${inputCls} w-40`} value={day} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDay(e.target.value)} />
          </label>
          <label className="text-xs text-muted">
            <span className="mb-1 block">Hours worn</span>
            <input type="number" min="0" max="24" step="0.5" className={`${inputCls} w-28`} value={hours} onChange={(e) => setHours(e.target.value)} placeholder="18" />
          </label>
          <label className="min-w-[12rem] flex-1 text-xs text-muted">
            <span className="mb-1 block">Note (optional)</span>
            <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reported by phone" />
          </label>
          <Btn tone="accent" disabled={busy || hours === '' || Number.isNaN(Number(hours))} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Record'}
          </Btn>
        </div>
      </section>

      <section className="rounded-lg border border-line bg-surface shadow-level-1">
        <table className="w-full text-sm">
          <thead className="border-b border-line bg-surface-2">
            <tr>
              <th className="px-3 py-2.5 text-left text-[12px] font-semibold text-muted">Day</th>
              <th className="px-3 py-2.5 text-left text-[12px] font-semibold text-muted">Worn</th>
              <th className="px-3 py-2.5 text-left text-[12px] font-semibold text-muted">Against goal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const pct = Math.min(100, Math.round((d.seconds / 3600 / c.targetHours) * 100));
              return (
                <tr key={d.day} className="border-t border-line">
                  <td className="tabnum whitespace-nowrap px-3 py-2">{formatDate(d.day)}</td>
                  <td className="tabnum px-3 py-2 font-medium text-ink">
                    {d.seconds > 0 ? `${wearHours(d.seconds)}h` : <span className="font-normal text-muted">nothing recorded</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2">
                      <span className="h-1.5 w-32 overflow-hidden rounded-full bg-surface-2">
                        <span
                          className={`block h-full rounded-full ${d.onTarget ? 'bg-accent' : 'bg-status-warn-fg/70'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </span>
                      <span className="tabnum text-xs text-muted">{pct}%</span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

/* -- photos, with A/B comparison ---------------------------------------------------------------- */

/**
 * Side-by-side comparison is the point of taking a series: one photo tells you nothing, tray 4
 * beside tray 12 tells you whether the teeth are tracking. Pick A and B from the grid.
 */
function PhotoReview({ c, urlFor }: { c: CaseCompliance; urlFor: (p: ProgressPhoto) => string }) {
  const [pair, setPair] = useState<[ProgressPhoto | null, ProgressPhoto | null]>([null, null]);
  const put = (p: ProgressPhoto, slot: 0 | 1) =>
    setPair((cur) => (slot === 0 ? [p, cur[1]] : [cur[0], p]));

  return (
    <div className="space-y-4">
      {(pair[0] || pair[1]) && (
        <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink">Compare</h2>
            <button type="button" onClick={() => setPair([null, null])} className="text-xs font-medium text-muted hover:text-ink">
              Clear
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {([0, 1] as const).map((slot) => {
              const p = pair[slot];
              return (
                <figure key={slot} className="min-w-0">
                  {p ? (
                    <>
                      <img src={urlFor(p)} alt={`${PHOTO_POSE_LABELS[p.pose]}, ${formatDate(p.takenAt)}`} className="w-full rounded-md border border-line object-cover" />
                      <figcaption className="mt-1.5 text-xs text-muted">
                        <b className="text-ink">{slot === 0 ? 'A' : 'B'}</b> · {PHOTO_POSE_LABELS[p.pose]}
                        {p.trayNo ? ` · tray ${p.trayNo}` : ''} · {formatDate(p.takenAt)}
                      </figcaption>
                    </>
                  ) : (
                    <div className="grid h-40 place-items-center rounded-md border border-dashed border-line text-xs text-muted">
                      Pick {slot === 0 ? 'A' : 'B'} below
                    </div>
                  )}
                </figure>
              );
            })}
          </div>
        </section>
      )}

      <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
        <h2 className="mb-3 text-sm font-semibold text-ink">Progress photos</h2>
        {c.photos.length === 0 ? (
          <p className="text-sm text-muted">
            The patient hasn&rsquo;t sent any photos yet. The app asks for a set each time a tray change
            is due.
          </p>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {c.photos.map((p) => (
              <li key={p.id} className="group relative">
                <img src={urlFor(p)} alt={`${PHOTO_POSE_LABELS[p.pose]}, ${formatDate(p.takenAt)}`} loading="lazy" className="h-28 w-full rounded-md border border-line object-cover" />
                <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  {([0, 1] as const).map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => put(p, slot)}
                      title={`Compare as ${slot === 0 ? 'A' : 'B'}`}
                      className="grid h-6 w-6 place-items-center rounded bg-surface/90 text-[11px] font-bold text-ink shadow-level-1"
                    >
                      {slot === 0 ? 'A' : 'B'}
                    </button>
                  ))}
                </div>
                <div className="mt-1 truncate text-[11px] text-muted">
                  {p.trayNo ? `Tray ${p.trayNo}` : formatDate(p.takenAt)} · {PHOTO_POSE_LABELS[p.pose]}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* -- timeline ------------------------------------------------------------------------------------ */

/** Wear and photos on one thread, so "she stopped wearing them after tray 6" is visible. */
function ComplianceTimeline({ c }: { c: CaseCompliance }) {
  const entries = [
    ...c.photos.map((p) => ({
      at: p.takenAt,
      title: `Photo - ${PHOTO_POSE_LABELS[p.pose]}${p.trayNo ? ` (tray ${p.trayNo})` : ''}`,
      tone: 'accent' as const,
    })),
    ...c.days
      .filter((d) => d.seconds > 0)
      .map((d) => ({
        at: `${d.day}T12:00:00`,
        title: `${wearHours(d.seconds)}h worn${d.onTarget ? '' : ' - under goal'}`,
        tone: d.onTarget ? ('good' as const) : ('warn' as const),
      })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  if (!entries.length) return <Empty>Nothing recorded yet.</Empty>;
  return (
    <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <ol className="relative space-y-3 border-l border-line pl-4">
        {entries.slice(0, 60).map((e, i) => (
          <li key={i}>
            <span
              className={`absolute -left-1 mt-1.5 h-2 w-2 rounded-full ${
                e.tone === 'accent' ? 'bg-accent' : e.tone === 'good' ? 'bg-status-progress-fg' : 'bg-status-warn-fg'
              }`}
            />
            <div className="text-xs text-muted">{formatDate(e.at)}</div>
            <div className="text-sm text-ink">{e.title}</div>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* -- reminder ------------------------------------------------------------------------------------ */

function RemindPatient({ caseId }: { caseId: string }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const send = async (kind: 'wear' | 'photos') => {
    setBusy(kind);
    setErr(null);
    try {
      await wfApi.remindPatient(caseId, kind);
      setSent(kind === 'wear' ? 'Wear reminder sent.' : 'Photo reminder sent.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <h2 className="text-sm font-semibold text-ink">Nudge the patient</h2>
      <p className="mb-3 mt-0.5 text-xs text-muted">
        Sends a notification to their phone and records it on the case. Use it sparingly - a patient
        who is pinged daily stops reading them.
      </p>
      {err && <div className="mb-2 text-sm text-status-urgent-fg">{err}</div>}
      {sent && <div className="mb-2 text-sm text-status-progress-fg">{sent}</div>}
      <div className="flex flex-wrap gap-2">
        <Btn tone="ghost" disabled={!!busy} onClick={() => void send('wear')}>
          {busy === 'wear' ? 'Sending…' : 'Remind about wear'}
        </Btn>
        <Btn tone="ghost" disabled={!!busy} onClick={() => void send('photos')}>
          {busy === 'photos' ? 'Sending…' : 'Ask for photos'}
        </Btn>
      </div>
    </section>
  );
}

/* -- the aligner count, and the approval share it gates ---------------------------------------- */

/**
 * The number of aligners has to be on the case before a setup goes to the doctor: the count sets
 * the price (PKR 2,500 a tray up to 20, PKR 2,000 above), and a doctor approving a setup is
 * approving what it costs. So this is offered inline wherever it blocks, with the resulting price
 * shown — nobody should have to go and find the field on another tab.
 */
export function TrayCountRequired({
  caseId,
  onSet,
  priority,
}: {
  caseId: string;
  onSet: () => void;
  /** Express delivery adds a surcharge, so the quoted total has to include it. */
  priority?: WfPriority | null;
}) {
  const [upper, setUpper] = useState('');
  const [lower, setLower] = useState('');
  const [discount, setDiscount] = useState<AlignerDiscount | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pricing = useAlignerPricing();
  const u = Math.max(0, Number(upper) || 0);
  const l = Math.max(0, Number(lower) || 0);
  const n = u + l;
  const valid = Number.isFinite(n) && n > 0 && n <= 200;
  const quote = valid ? alignerQuote(n, pricing, discount) : null;
  const price = quote?.gross ?? null;
  const rush = turnaroundSurchargeRupees(priority);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await wfApi.patch(caseId, { upperTrays: u, lowerTrays: l, trays: n, discountType: discount?.type ?? null, discountValue: discount?.value ?? null });
      onSet();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-status-warn-fg/30 bg-status-warn-bg/50 p-3">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 shrink-0 text-status-warn-fg">
          <IconAlert size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <b className="text-sm text-status-warn-fg">How many aligners - upper / lower?</b>
          <p className="mt-0.5 text-xs text-status-warn-fg/90">
            Set the upper and lower numbers before sharing the setup - they go on the case proforma the
            doctor approves, and they decide the clinic price.
          </p>
          <div className="mt-2">
            <ArchTrayInputs upper={upper} lower={lower} onChange={(a, b) => { setUpper(a); setLower(b); }} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs text-status-warn-fg/90">Discount</span>
            <DiscountInput value={discount} onChange={setDiscount} />
            <Btn tone="accent" disabled={!valid || busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save count'}
            </Btn>
          </div>
          {price && quote && (
            <p className="tabnum mt-1.5 text-xs text-status-warn-fg">
              {price.trays} x PKR {price.perTrayRupees.toLocaleString()}
              {quote.discountPaisa > 0 ? ` - PKR ${quote.discountRupees.toLocaleString()} discount` : ''}
              {rush > 0 ? ` + PKR ${rush.toLocaleString()} express` : ''} ={' '}
              <b>PKR {(quote.netRupees + rush).toLocaleString()}</b>
              {price.tier === 'bulk' ? ' (bulk rate)' : ''}
            </p>
          )}
          {err && <p className="mt-1.5 text-xs text-status-urgent-fg">{err}</p>}
        </div>
      </div>
    </div>
  );
}

/* -- share a model video for approval ---------------------------------------------------------- */

/** Files worth putting in front of a doctor. A scan or an STL is raw material, not a review item. */
const SHAREABLE = new Set(['video', 'setup', 'treatment_plan', 'screenshot', 'image', 'pdf']);

export function ShareForApproval({
  caseId,
  files,
  trays,
  priority,
  onChanged,
}: {
  caseId: string;
  files: WfFile[];
  /** Aligner count on the case. Sharing is blocked until it is set. */
  trays?: number | null;
  priority?: WfPriority | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const shared = files.find((f) => f.sharedForApproval) ?? null;
  const candidates = files.filter((f) => SHAREABLE.has(f.category) && f.id !== shared?.id);
  const hasTrays = !!trays && trays > 0;

  const set = async (fileId: string, on: boolean) => {
    setBusy(fileId);
    setErr(null);
    try {
      await wfApi.shareForApproval(caseId, fileId, on, on ? note.trim() || null : null);
      setNote('');
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <h2 className="text-sm font-semibold text-ink">Shared with the doctor for approval</h2>
      <p className="mb-3 mt-0.5 text-xs text-muted">
        The doctor sees this at the top of their case, playing in place - no download. One artifact at
        a time; sharing another replaces it.
      </p>
      {err && <div className="mb-2 text-sm text-status-urgent-fg">{err}</div>}
      {!hasTrays && (
        <div className="mb-3">
          <TrayCountRequired caseId={caseId} onSet={onChanged} priority={priority} />
        </div>
      )}

      {shared ? (
        <div className="rounded-md border border-accent/40 bg-accent/5 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 truncate font-medium text-ink">{shared.originalName}</span>
            <span className="text-xs text-muted">{formatFileSize(shared.sizeBytes)}</span>
            <a
              className="rounded border border-line px-2 py-0.5 text-xs hover:bg-surface-2"
              href={fileDownloadUrl(caseId, shared.id, true)}
              target="_blank"
              rel="noreferrer"
            >
              Preview
            </a>
            <Btn tone="ghost" disabled={busy === shared.id} onClick={() => void set(shared.id, false)}>
              {busy === shared.id ? 'Withdrawing…' : 'Withdraw'}
            </Btn>
          </div>
          <div className="mt-1 text-xs text-muted">
            Shared {fmtDT(shared.sharedAt)}
            {shared.shareNote ? ` · “${shared.shareNote}”` : ''}
          </div>
        </div>
      ) : (
        <p className="mb-3 text-sm text-muted">Nothing is shared. The doctor has nothing to approve yet.</p>
      )}

      {candidates.length > 0 && (
        <div className="mt-3">
          <input
            className={inputCls}
            placeholder="Optional message for the doctor (shown above the video)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
          />
          <ul className="mt-2 space-y-1.5">
            {candidates.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center gap-2 rounded-md border border-line px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm text-ink">{f.originalName}</span>
                <span className="text-xs capitalize text-muted">{f.category.replace(/_/g, ' ')}</span>
                <span className="text-xs text-muted">{formatFileSize(f.sizeBytes)}</span>
                <Btn tone="accent" disabled={busy === f.id || !hasTrays} onClick={() => void set(f.id, true)}>
                  {busy === f.id ? 'Sharing…' : shared ? 'Share instead' : 'Share for approval'}
                </Btn>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!candidates.length && !shared && (
        <Empty>Upload the model video or simulation on the Files tab, then share it from here.</Empty>
      )}
    </section>
  );
}

/* -- the chase queue --------------------------------------------------------------------------- */

type QueueTab = 'flagged' | 'all';

export function WfCompliancePage() {
  const [q, setQ] = useState<ComplianceQueue | null>(null);
  const [tab, setTab] = useState<QueueTab>('flagged');
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    wfApi
      .complianceQueue()
      .then((r) => {
        setQ(r);
        setErr(null);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);

  const items = q ? (tab === 'flagged' ? q.items.filter((i) => i.lowWear) : q.items) : [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Aligner compliance"
        subtitle="Patients on the app, worst wear first. Wear is recorded by the patient's own timer - chase, don't diagnose."
      />
      {err && <div className="text-sm text-status-urgent-fg">{err}</div>}

      <Tabs<QueueTab>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'flagged', label: 'Needs chasing', count: q?.flagged ?? 0 },
          { key: 'all', label: 'All app patients', count: q?.items.length ?? 0 },
        ]}
      />

      {!q && <SkeletonList rows={5} />}
      {q && !items.length && (
        <Empty>
          {tab === 'flagged'
            ? 'Nobody is behind on wear right now.'
            : 'No cases have a patient app yet. Issue a patient login from a case to start tracking.'}
        </Empty>
      )}

      {items.length > 0 && (
        <ul className="stagger space-y-2">
          {items.map((i) => (
            <li key={i.caseId}>
              <Link
                to={`/workflow/cases/${i.caseId}`}
                className="tap-row flex items-center gap-3 rounded-lg border border-line bg-surface p-3.5 shadow-level-1"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-semibold uppercase tracking-tight text-ink">{i.patientName}</span>
                    <ComplianceBadge adherence={i.adherence7} small />
                    {i.lowWear && (
                      <span className="inline-flex items-center gap-1 text-status-warn-fg" title="Low wear">
                        <IconAlert size={15} />
                      </span>
                    )}
                    {i.wearingNow && <span className="text-[11px] font-medium text-status-progress-fg">wearing now</span>}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted">
                    <span className="font-mono">{i.caseNumber}</span>
                    <span className="tabnum">{i.avgHours7}h/day avg</span>
                    <span className="tabnum">
                      {i.daysBelowTarget7}/7 days short
                    </span>
                    {i.currentTray != null && (
                      <span className="tabnum">
                        tray {i.currentTray}
                        {i.totalTrays ? `/${i.totalTrays}` : ''}
                      </span>
                    )}
                    <span>{i.lastWornDay ? `last worn ${formatDate(i.lastWornDay)}` : 'never started'}</span>
                    {i.photoCount > 0 && <span className="tabnum">{i.photoCount} photos</span>}
                  </div>
                  <div className="truncate text-xs text-muted">{i.doctorName ?? i.clinicName ?? '-'}</div>
                </div>
                <span className="shrink-0 text-muted">
                  <IconChevronRight size={18} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
